<template>
  <div>
    <div v-if="ppaPortfoliosWithInstructions.length > 0">
      <div
        v-for="(portfolio, portfolioIndex) in ppaPortfoliosWithInstructions"
        :key="portfolio.id || `portfolio-${portfolioIndex}`"
        class="mb-4"
      >
        <PortfolioInstructionsEditable
          :usesCnmcIssuance="hasCnmcConnection(company.registry_connections)"
          :portfolio="portfolio"
          :registryDevices="registryDevices"
          :existingPortfolios="ppaPortfoliosWithInstructions"
          :isGlobalEditingActive="isAnyEditingActive"
          @save="
            (updatedPortfolio) =>
              handlePortfolioSave(updatedPortfolio, portfolioIndex)
          "
          @editingStateChange="
            (isEditing: boolean) =>
              handlePortfolioEditingStateChange(portfolioIndex, isEditing)
          "
        />
      </div>
    </div>

    <!-- Add New Portfolio UI -->
    <div v-if="!isCreatingNewPortfolio" class="mb-4">
      <button
        class="btn btn-outline-primary"
        @click="startCreatingNewPortfolio"
        :disabled="isAnyEditingActive"
      >
        <FontAwesomeIcon icon="fas fa-plus" class="me-1" />
        {{ $t("common.add") }}
      </button>
    </div>

    <div v-if="isCreatingNewPortfolio" class="mb-4">
      <PortfolioInstructionsEditable
        :usesCnmcIssuance="hasCnmcConnection(company.registry_connections)"
        :portfolio="newPortfolio"
        :registryDevices="registryDevices"
        :existingPortfolios="ppaPortfoliosWithInstructions"
        :isGlobalEditingActive="false"
        :startInEditMode="true"
        @save="handleNewPortfolioSave"
        @cancel="cancelNewPortfolio"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { hasCnmcConnection } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcPresentation";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  createEmptyPortfolioInstructions,
  filterPortfoliosWithInstructions,
} from "@/composables/usePortfolioUtils";
import PortfolioInstructionsEditable from "@/dashboard/production_strategy/overview/PortfolioInstructionsEditable.vue";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import type {
  Company,
  PortfolioWithInstructions,
  RegistryDevice,
} from "@/types";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const productionStore = useProductionStrategyStore();
const { t } = useI18n();

const props = defineProps<{
  company: Company;
  registryDevices: RegistryDevice[];
}>();

// Helper function to create a new empty PPA portfolio
const createEmptyPPAPortfolio = (): PortfolioWithInstructions => ({
  name: "",
  portfolio_type: "PPA",
  is_default: false,
  company_id: props.company.id!,
  portfolio_instructions: createEmptyPortfolioInstructions(),
  custom_user_upload_id: null,
});

// Global edit state management
const isCreatingNewPortfolio = ref<boolean>(false);
const newPortfolio = ref<PortfolioWithInstructions>(createEmptyPPAPortfolio());
const activeEditingPortfolios = ref<Set<number>>(new Set());

const ppaPortfolios = computed(() => {
  // Convert readonly portfolios to mutable for compatibility
  const mutablePortfolios = JSON.parse(
    JSON.stringify(productionStore.portfolios)
  );
  return mutablePortfolios.filter((p: any) => p.portfolio_type === "PPA");
});

const ppaPortfoliosWithInstructions = computed(() => {
  return filterPortfoliosWithInstructions(ppaPortfolios.value);
});

const isAnyEditingActive = computed(() => {
  return isCreatingNewPortfolio.value || activeEditingPortfolios.value.size > 0;
});

const handlePortfolioSave = async (
  updatedPortfolio: PortfolioWithInstructions,
  _portfolioIndex: number
) => {
  if (!props.company.id) return;

  try {
    const success = await productionStore.savePortfolio(
      props.company.id,
      updatedPortfolio
    );
    if (success) {
      globalSuccessHandler(
        t("portfolio_instructions.update_success_title"),
        t("portfolio_instructions.update_success")
      );
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

const handlePortfolioEditingStateChange = (
  portfolioIndex: number,
  isEditing: boolean
) => {
  if (isEditing) {
    activeEditingPortfolios.value.add(portfolioIndex);
  } else {
    activeEditingPortfolios.value.delete(portfolioIndex);
  }
};

const startCreatingNewPortfolio = () => {
  if (isAnyEditingActive.value) return;
  isCreatingNewPortfolio.value = true;
  newPortfolio.value = createEmptyPPAPortfolio();
};

const handleNewPortfolioSave = async (
  updatedPortfolio: PortfolioWithInstructions
) => {
  if (!props.company.id) return;

  try {
    const success = await productionStore.savePortfolio(
      props.company.id,
      updatedPortfolio
    );
    if (success) {
      isCreatingNewPortfolio.value = false;
      globalSuccessHandler(
        t("portfolio_instructions.update_success_title"),
        t("portfolio_instructions.update_success")
      );
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

const cancelNewPortfolio = () => {
  isCreatingNewPortfolio.value = false;
  newPortfolio.value = createEmptyPPAPortfolio();
};
</script>
